import { formatDollars } from '@fruitful/fruitils';
import {
    type AhaResult,
    type DebtAhaResult,
    type SavingsAhaResult,
} from '@fruitful/types';
import React from 'react';
import Animated, {
    Easing,
    FadeInDown,
    useAnimatedStyle,
    withRepeat,
    withSequence,
    withTiming,
} from 'react-native-reanimated';
import {
    ArrowDown,
    BodyMediumRegular,
    FontFamilyName,
    FruitfulLogoIcon,
    Icon,
    Image,
    isNonMobileWeb,
    LabelRegular,
    Text,
    useThemeColors,
    View,
    YStack,
} from 'smoothie';

import { getAhaMomentImageSource } from '../utils/ahaMomentImages';

const BOUNCE_CONFIG = { duration: 500, easing: Easing.inOut(Easing.ease) };
/** Slow entrance after Cultivating fades — ~1s longer than the previous spring. */
const AHA_MOMENT_ENTER_MS = 1800;

const BouncingArrow = () => {
    const bounceStyle = useAnimatedStyle(() => ({
        transform: [
            {
                translateY: withRepeat(
                    withSequence(
                        withTiming(-3, BOUNCE_CONFIG),
                        withTiming(-3, { duration: 250 }),
                        withTiming(3, BOUNCE_CONFIG),
                        withTiming(3, { duration: 250 }),
                    ),
                    -1,
                    true,
                ),
            },
        ],
    }));

    return (
        <Animated.View style={bounceStyle}>
            <Icon icon={ArrowDown} />
        </Animated.View>
    );
};

const useIntroText = (ahaResult: AhaResult): string => {
    if (ahaResult.type === 'investing') return '';
    if (ahaResult.debtFreeDate !== '') {
        const suffix = ahaResult.type === 'debt' ? ' be\u2026' : '\u2026';
        return `Your Money Map is ready! Based on everything you\u2019ve told us, we estimate that you could${suffix}`;
    }
    return 'Your Money Map is ready!';
};

const StandardAhaContent = ({
    ahaResult,
    imageSource,
    introText,
}: {
    ahaResult: DebtAhaResult | SavingsAhaResult;
    imageSource: ReturnType<typeof getAhaMomentImageSource>;
    introText: string;
}) => {
    const { getColor } = useThemeColors();
    return (
        <YStack
            alignItems="center"
            justifyContent="center"
            gap={24}
            marginTop="auto"
        >
            {!isNonMobileWeb && (
                <FruitfulLogoIcon
                    width={48}
                    height={41}
                    color={getColor('$fruitfulGreen')}
                />
            )}
            <BodyMediumRegular textAlign="center" paddingHorizontal={24}>
                {introText}
            </BodyMediumRegular>
            <Text
                fontSize={70}
                lineHeight={62}
                letterSpacing="-3.5px"
                textAlign="center"
                style={{ fontFamily: FontFamilyName.SourceSerifRegular }}
            >
                {ahaResult.displayHeadline}
                {ahaResult.debtFreeDate !== '' && `\n${ahaResult.debtFreeDate}`}
            </Text>
            <Image
                source={imageSource}
                style={{ width: 280, height: 280 }}
                resizeMode="contain"
            />
        </YStack>
    );
};

const InvestingAhaContent = ({
    imageSource,
    projectedTotalFormatted,
    projectedDate,
}: {
    imageSource: ReturnType<typeof getAhaMomentImageSource>;
    projectedTotalFormatted: string;
    projectedDate: string;
}) => {
    const { getColor } = useThemeColors();
    return (
        <YStack
            alignItems="center"
            justifyContent="center"
            gap={24}
            marginTop="auto"
        >
            {!isNonMobileWeb && (
                <FruitfulLogoIcon
                    width={48}
                    height={41}
                    color={getColor('$fruitfulGreen')}
                />
            )}
            <BodyMediumRegular textAlign="center" paddingHorizontal={24}>
                Your optimized investment system is ready! Based on everything
                you&apos;ve told us, we estimate you could have...
            </BodyMediumRegular>
            <Text
                fontSize={70}
                lineHeight={62}
                letterSpacing="-3.5px"
                textAlign="center"
                style={{ fontFamily: FontFamilyName.SourceSerifRegular }}
            >
                {projectedTotalFormatted}
                {'\n'}invested by{'\n'}
                {projectedDate}
            </Text>
            <Image
                source={imageSource}
                style={{ width: 280, height: 280 }}
                resizeMode="contain"
            />
        </YStack>
    );
};

const AhaMoment = ({
    ahaResult,
    navVisible,
    showScrollHint,
    skipEnteringAnimation = false,
}: {
    ahaResult: AhaResult;
    navVisible: boolean;
    showScrollHint: boolean;
    skipEnteringAnimation?: boolean;
}) => {
    const imageSource = getAhaMomentImageSource(ahaResult.image);
    const introText = useIntroText(ahaResult);

    return (
        <View
            flex={1}
            alignItems="center"
            opacity={navVisible ? 0 : 1}
            style={{
                transition: 'opacity 0.4s ease',
            }}
        >
            <Animated.View
                entering={
                    skipEnteringAnimation
                        ? undefined
                        : FadeInDown.duration(AHA_MOMENT_ENTER_MS).easing(
                              Easing.out(Easing.cubic),
                          )
                }
                style={{
                    flex: 1,
                    width: '100%',
                    alignItems: 'center',
                }}
            >
                <YStack
                    flex={1}
                    width="100%"
                    alignItems="center"
                    justifyContent="center"
                >
                    {ahaResult.type === 'investing' ? (
                        <InvestingAhaContent
                            imageSource={imageSource}
                            projectedTotalFormatted={formatDollars(
                                Math.round(
                                    ahaResult.projectedTotalCents / 100 / 1000,
                                ) * 1000,
                                false,
                            )}
                            projectedDate={ahaResult.projectedDate}
                        />
                    ) : (
                        <StandardAhaContent
                            ahaResult={ahaResult}
                            imageSource={imageSource}
                            introText={introText}
                        />
                    )}
                    <YStack
                        marginTop="auto"
                        gap={14}
                        paddingTop={isNonMobileWeb ? 8 : 24}
                        paddingBottom={isNonMobileWeb ? 40 : 120}
                        justifyContent="center"
                        alignItems="center"
                        animation="slow"
                        opacity={showScrollHint ? 1 : 0}
                    >
                        <LabelRegular>SCROLL</LabelRegular>
                        <BouncingArrow />
                    </YStack>
                </YStack>
            </Animated.View>
        </View>
    );
};

export default AhaMoment;
